import { useState } from 'react'
import { Eye, EyeOff, Check, X } from 'lucide-react'

export interface PasswordFieldProps {
  id?: string
  name?: string
  label?: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  required?: boolean
  disabled?: boolean
  autoComplete?: string
  minLength?: number
  showRequirements?: boolean
  helperText?: string
  className?: string
  inputClassName?: string
  error?: string
}

export interface PasswordRequirement {
  label: string
  valid: boolean
}

export function getPasswordRequirements(password: string): PasswordRequirement[] {
  return [
    { label: 'At least 8 characters', valid: password.length >= 8 },
    { label: 'Contains uppercase letter (A-Z)', valid: /[A-Z]/.test(password) },
    { label: 'Contains lowercase letter (a-z)', valid: /[a-z]/.test(password) },
    { label: 'Contains number (0-9)', valid: /\d/.test(password) },
    { label: 'Contains special character (!@#$%^&*...)', valid: /[!@#$%^&*()_+\-=\[\]{}|;':",./<>?]/.test(password) },
  ]
}

export function isPasswordValid(password: string): boolean {
  return getPasswordRequirements(password).every(r => r.valid)
}

export function PasswordField({
  id,
  name,
  label,
  value,
  onChange,
  placeholder = '••••••••',
  required = false,
  disabled = false,
  autoComplete = 'current-password',
  minLength = 8,
  showRequirements = false,
  helperText,
  className = '',
  inputClassName = '',
  error,
}: PasswordFieldProps) {
  const [showPassword, setShowPassword] = useState(false)
  const requirements = getPasswordRequirements(value)

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {label && (
        <label htmlFor={id} className="text-sm font-medium text-text-primary">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}

      <div className="relative">
        <input
          id={id}
          name={name}
          type={showPassword ? 'text' : 'password'}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          autoComplete={autoComplete}
          minLength={minLength}
          className={`input w-full pr-10 ${error ? 'border-red-500 focus:border-red-500' : ''} ${inputClassName}`}
        />
        <button
          type="button"
          onClick={() => setShowPassword(prev => !prev)}
          disabled={disabled}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-primary transition-colors focus:outline-none"
          title={showPassword ? 'Hide password' : 'Show password'}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
          tabIndex={-1}
        >
          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>

      {error && <span className="text-xs text-red-500 dark:text-red-400">{error}</span>}

      {helperText && !error && (
        <span className="text-xs text-text-tertiary">{helperText}</span>
      )}

      {showRequirements && value.length > 0 && (
        <div className="mt-2 space-y-1 rounded-lg border border-border-secondary bg-surface-secondary/40 p-2.5">
          <p className="text-xs font-medium text-text-secondary">Password requirements:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 mt-1">
            {requirements.map((req, idx) => (
              <div key={idx} className="flex items-center gap-1.5 text-xs">
                {req.valid ? (
                  <Check className="w-3.5 h-3.5 text-green-600 dark:text-green-400 flex-shrink-0" />
                ) : (
                  <X className="w-3.5 h-3.5 text-text-tertiary flex-shrink-0" />
                )}
                <span
                  className={
                    req.valid
                      ? 'text-green-600 dark:text-green-400 font-medium'
                      : 'text-text-tertiary'
                  }
                >
                  {req.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
